<template lang="pug">
    .smtp-credentials.smtp-credentials--sectioned
        h4.mailbox-heading(v-if="!inline")
            i(:class="scope === 'newsletterSmtp' ? 'el-icon-fa-paper-plane fas' : 'el-icon-fa-envelope fas'")
            | {{ scope === 'newsletterSmtp' ? $t('Newsletter SMTP credentials') : $t('Email mailbox') }}

        .mailbox-section.mailbox-auth(v-if="showOAuth")
            .mailbox-section__title
                span.mailbox-section__label
                    i.el-icon-fa-key.fas
                    | {{ $t('Authentication') }}
            el-form-item.auth-method
                el-select.is-full-width(v-model="authMethod")
                    el-option(:label="$t('Password (SMTP/IMAP)')" value="password")
                    el-option(:label="$t('Google (OAuth)')" value="oauth_google")
                    el-option(:label="$t('Microsoft 365 (OAuth)')" value="oauth_microsoft")
                span.help(v-if="isMailboxScope") {{ $t('Applies to both sending (SMTP) and receiving (IMAP).') }}
            .oauth-connect(v-if="isOauth")
                el-alert.oauth-status(
                    v-if="oauthPending"
                    type="success"
                    :closable="false"
                    show-icon
                    :title="oauthPendingEmail ? $t('Connected as {email} - saved with the form.', { email: oauthPendingEmail }) : $t('Connected - saved with the form.')"
                )
                el-alert.oauth-status(
                    v-else-if="isOauthConnected"
                    type="success"
                    :closable="false"
                    show-icon
                    :title="$t('Connected via') + ' ' + oauthProviderLabel"
                )
                el-alert.oauth-status(
                    v-else
                    type="info"
                    :closable="false"
                    show-icon
                    :title="$t('Connect your {provider} account to grant access.', { provider: oauthProviderLabel })"
                )
                el-button.oauth-button(
                    type="primary"
                    plain
                    size="small"
                    :loading="connectingOauth"
                    icon="fas el-icon-fa-arrow-right-to-bracket"
                    @click="connectOauth('user')"
                ) {{ oauthButtonLabel }}
                span.help {{ $t('Opens the provider consent screen in a popup. Host/port are detected automatically.') }}
                //- Microsoft 365 tenants often block user consent ("Administratorgenehmigung
                //- erforderlich"). A tenant admin grants the app org-wide ONCE here; then
                //- every user connects normally. No effect for Gmail.
                .oauth-admin(v-if="isMicrosoftOauth && !isOauthConnected")
                    span.help {{ $t('Does your organization require administrator approval?') }}
                    el-button.oauth-admin__button(
                        type="text"
                        size="mini"
                        icon="fas el-icon-fa-shield-halved"
                        @click="connectOauth('admin')"
                    ) {{ $t('Approve for your organization') }}

        .mailbox-section
            .mailbox-section__title
                span.mailbox-section__label
                    i.el-icon-fa-paper-plane.fas
                    | {{ isOauth ? $t('Sender') : $t('Sending (SMTP)') }}
                .smtp-validation-status(v-if="(isExistingCredentials || validationStatus) && !isOauth")
                    el-tag.status-tag(
                        v-if="validationStatus === 'valid'"
                        type="success"
                        size="small"
                    ) {{ $t('Verified') }}
                    el-tag.status-tag(
                        v-else-if="validationStatus === 'invalid'"
                        type="danger"
                        size="small"
                    ) {{ $t('Faulty') }}
                    el-tag.status-tag(
                        v-else
                        type="info"
                        size="small"
                    ) {{ $t('Not verified') }}
            .flex-row
                el-form-item.email.grow(v-if="!isOauth" :label="$t('Email')")
                    el-input(type="email" v-model="address" :placeholder="addressPlaceholder" maxlength="255" show-word-limit)
                el-form-item.name.grow.no-margin(:label="isOauth ? $t('Display name (optional)') : $t('Name')")
                    el-input(type="input" v-model="name" :placeholder="namePlaceholder" maxlength="255" show-word-limit)
                    span.help(v-if="isOauth") {{ $t('Shown to recipients in the From line - sent from your connected account.') }}
            template(v-if="!isOauth")
                .host-and-port
                    el-form-item.host(:label="$t('Host')" prop="host")
                        el-input(type="input" v-model="host" :placeholder="hostPlaceholder" maxlength="255" show-word-limit)
                    el-form-item.port(:label="$t('Port')" prop="port")
                        el-input(type="number" v-model="port" :placeholder="portPlaceholder")
                .user-and-pass
                    el-form-item.user(:label="$t('User name')" prop="username")
                        el-input(type="input" v-model="username" :placeholder="usernamePlaceholder" maxlength="255" show-word-limit)
                    .password-field
                        el-form-item.switch(v-if="isExistingCredentials" size="mini")
                            el-switch(
                                :active-text="$t('Change password')"
                                inactive-color="#ff4949"
                                v-model="changePasswordEnabled"
                            )
                        el-form-item.pass(
                            v-if="!isExistingCredentials || changePasswordEnabled"
                            :label="$t('Password')"
                            prop="password"
                        )
                            el-input(
                                type="password"
                                v-model="password"
                                maxlength="255"
                                show-word-limit
                                show-password
                                ref="passwordInput"
                            )
                .mailgun-webhook-signing-key
                    el-form-item.pass(:label="$t('Mailgun Webhook Signing Key')")
                        el-input(type="input" v-model="mailgunWebhookSigningKey" maxlength="255" show-word-limit)
            .smtp-test(v-if="!isOauth && (isExistingCredentials || hasRequiredFields)")
                el-form-item.recipient-email(:label="$t('SMTP-Test recipient')")
                    el-input(
                        v-model="testRecipientEmail"
                        size="small"
                    )
                el-button.test-button(
                    size="small"
                    type="primary"
                    plain
                    :loading="testingSmtp"
                    :disabled="!hasRequiredFields || !isValidRecipientEmail"
                    icon="fas el-icon-fa-paper-plane"
                    @click="testSmtpCredentials"
                ) {{ $t('Test SMTP') }}

        .mailbox-section(v-if="showMailboxSections")
            .mailbox-section__title
                span.mailbox-section__label
                    i.el-icon-fa-inbox.fas
                    | {{ isOauth ? $t('Receiving') : $t('Receiving (IMAP)') }}
                .smtp-validation-status(v-if="(isExistingCredentials || imapValidationStatus) && !isOauth")
                    el-tag.status-tag(
                        v-if="imapValidationStatus === 'valid'"
                        type="success"
                        size="small"
                    ) {{ $t('Verified') }}
                    el-tag.status-tag(
                        v-else-if="imapValidationStatus === 'invalid'"
                        type="danger"
                        size="small"
                    ) {{ $t('Faulty') }}
                    el-tag.status-tag(
                        v-else
                        type="info"
                        size="small"
                    ) {{ $t('Not verified') }}
            .imap-inbox
                //- Password auth: receiving is opt-in (a send-only relay may have no inbox).
                el-form-item.switch(v-if="!isOauth")
                    el-switch(:active-text="$t('Receive replies in Messages (IMAP)')" v-model="imapEnabled")
                //- OAuth grants IMAP on the same token, so receiving is simply on.
                el-alert.imap-status(
                    v-if="isOauth && (oauthPending || isOauthConnected)"
                    type="success"
                    :closable="false"
                    show-icon
                    :title="$t('Receiving is active - replies appear in the Messages inbox.')"
                )
                span.help(v-if="isOauth && !oauthPending && !isOauthConnected") {{ $t('Activated automatically once connected.') }}
                template(v-if="imapEnabled")
                    el-alert.imap-hint(
                        v-if="!isOauth"
                        type="info"
                        :closable="false"
                        show-icon
                        :title="$t('Polls this mailbox so replies to your system emails appear in the Messages inbox.')"
                    )
                    .host-and-port(v-if="!isOauth")
                        el-form-item.host(:label="$t('IMAP Host')")
                            el-input(type="input" v-model="imapHost" maxlength="255" show-word-limit)
                        el-form-item.port(:label="$t('Port')")
                            el-input(type="number" v-model="imapPort")
                    el-form-item(v-if="!isOauth" :label="$t('Encryption')")
                        el-select.is-full-width(v-model="imapEncryption")
                            el-option(label="SSL" value="ssl")
                            el-option(label="TLS" value="tls")
                            el-option(label="STARTTLS" value="starttls")
                            el-option(:label="$t('None')" value="none")
                    template(v-if="!isOauth")
                        el-form-item.switch.imap-sep-toggle
                            el-switch(:active-text="$t('Use a separate IMAP login')" v-model="useSeparateImapCredentials")
                        template(v-if="useSeparateImapCredentials")
                            el-form-item.user(:label="$t('IMAP user name')")
                                el-input(type="input" v-model="imapUsername" :placeholder="username" maxlength="255" show-word-limit)
                            el-form-item.imap-pass(:label="$t('IMAP password')")
                                el-input(type="password" v-model="imapPassword" show-password)
                                span.help {{ $t('For a send-only relay like Mailgun the inbox is a separate mailbox with its own login.') }}
                        span.help.imap-reuse-hint(v-else) {{ $t('IMAP uses the SMTP user name and password.') }}
                    .imap-test(v-if="!isOauth")
                        el-button.test-button(
                            size="small"
                            type="primary"
                            plain
                            :loading="testingImap"
                            :disabled="!hasImapRequiredFields"
                            icon="fas el-icon-fa-inbox"
                            @click="testImapCredentials"
                        ) {{ $t('Test IMAP') }}


        .mailbox-section(v-if="showMailboxSections && showSignature")
            .mailbox-section__title
                span.mailbox-section__label
                    i.el-icon-fa-pen.fas
                    | {{ $t('Signature') }}
            .default-signature
                el-form-item.no-margin(:label="$t('Default signature')")
                    el-select.is-full-width(v-model="defaultSignatureId" clearable :placeholder="$t('No signature')")
                        el-option(v-for="signature in signatures" :key="signature.id" :label="signature.name" :value="signature.id")
                    span.help {{ $t('Appended to outgoing mail sent from this mailbox. Manage signatures under Customer Communication.') }}
</template>

<i18n>
{
    "de": {
        "SMTP credentials": "SMTP Zugangsdaten",
        "Newsletter SMTP credentials": "Newsletter SMTP Zugangsdaten",
        "Host": "Host",
        "Port": "Port",
        "User name": "Benutzername",
        "Password": "Passwort",
        "Email": "E-Mail",
        "Change password": "Passwort ändern",
        "Test SMTP": "SMTP testen",
        "Verified": "Verifiziert",
        "Faulty": "Fehlerhaft",
        "Not verified": "Nicht verifiziert",
        "SMTP-Test recipient": "SMTP-Test Empfänger",
        "SMTP test successful": "SMTP Test erfolgreich",
        "SMTP test failed": "SMTP Test fehlgeschlagen",
        "Test IMAP": "IMAP testen",
        "IMAP test successful": "IMAP Test erfolgreich",
        "IMAP test failed": "IMAP Test fehlgeschlagen",
        "Receive replies in Messages (IMAP)": "Antworten in Nachrichten empfangen (IMAP)",
        "Polls this mailbox so replies to your system emails appear in the Messages inbox.": "Ruft dieses Postfach ab, damit Antworten auf System-E-Mails im Nachrichten-Posteingang erscheinen.",
        "IMAP Host": "IMAP Host",
        "IMAP user name": "IMAP Benutzername",
        "IMAP password": "IMAP Passwort",
        "Use a separate IMAP login": "Separaten IMAP-Login verwenden",
        "For a send-only relay like Mailgun the inbox is a separate mailbox with its own login.": "Bei einem reinen Versand-Relay wie Mailgun ist der Posteingang ein separates Postfach mit eigenem Login.",
        "IMAP uses the SMTP user name and password.": "IMAP verwendet Benutzername und Passwort von SMTP.",
        "IMAP authorises through OAuth - no password needed.": "IMAP autorisiert über OAuth - kein Passwort nötig.",
        "Email mailbox": "E-Mail-Postfach",
        "The address the shop sends system emails from and receives replies on.": "Die Adresse, von der der Shop System-E-Mails versendet und auf der Antworten empfangen werden.",
        "Authentication": "Authentifizierung",
        "Password (SMTP/IMAP)": "Passwort (SMTP/IMAP)",
        "Google (OAuth)": "Google (OAuth)",
        "Microsoft 365 (OAuth)": "Microsoft 365 (OAuth)",
        "Applies to both sending (SMTP) and receiving (IMAP).": "Gilt für Senden (SMTP) und Empfangen (IMAP).",
        "Applies to sending (SMTP).": "Gilt für das Senden (SMTP).",
        "Connected via": "Verbunden über",
        "Connect your {provider} account to grant access.": "Verbinde dein {provider}-Konto, um Zugriff zu gewähren.",
        "Connected as {email} - saved with the form.": "Verbunden als {email} - wird mit dem Formular gespeichert.",
        "Connected - saved with the form.": "Verbunden - wird mit dem Formular gespeichert.",
        "Connected": "Verbunden",
        "Save to finish.": "Zum Abschließen speichern.",
        "Connected.": "Verbunden.",
        "Sender": "Absender",
        "Receiving": "Empfang",
        "Display name (optional)": "Anzeigename (optional)",
        "Shown to recipients in the From line - sent from your connected account.": "Wird Empfängern in der Absenderzeile angezeigt - gesendet über dein verbundenes Konto.",
        "Receiving is active - replies appear in the Messages inbox.": "Empfang aktiv - Antworten erscheinen im Nachrichten-Posteingang.",
        "Activated automatically once connected.": "Wird nach dem Verbinden automatisch aktiviert.",
        "Connect": "Verbinden",
        "Reconnect": "Erneut verbinden",
        "Opens the provider consent screen in a popup. Host/port are detected automatically.": "Öffnet die Zustimmungsseite des Anbieters in einem Popup. Host/Port werden automatisch erkannt.",
        "Does your organization require administrator approval?": "Erfordert deine Organisation eine Administratorgenehmigung?",
        "Approve for your organization": "Für deine Organisation freigeben",
        "Organization approved": "Organisation freigegeben",
        "Now click Connect to sign in the mailbox.": "Klicke jetzt auf Verbinden, um das Postfach anzumelden.",
        "Administrator approval required": "Administratorgenehmigung erforderlich",
        "Your Microsoft administrator must approve this app once. Use \"Approve for your organization\".": "Dein Microsoft-Administrator muss diese App einmalig freigeben. Nutze „Für deine Organisation freigeben“.",
        "Sign-in link expired": "Anmeldelink abgelaufen",
        "Please click Connect again to finish signing in.": "Bitte klicke erneut auf Verbinden, um die Anmeldung abzuschließen.",
        "Could not start the connection": "Verbindung konnte nicht gestartet werden",
        "Encryption": "Verschlüsselung",
        "None": "Keine",
        "Sending (SMTP)": "Versand (SMTP)",
        "Receiving (IMAP)": "Empfang (IMAP)",
        "Signature": "Signatur",
        "Default signature": "Standard-Signatur",
        "No signature": "Keine Signatur",
        "Appended to outgoing mail sent from this mailbox. Manage signatures under Customer Communication.": "Wird an ausgehende E-Mails dieses Postfachs angehängt. Signaturen werden unter Kommunikation verwaltet."
    },
    "en": {
        "Connect your {provider} account to grant access.": "Connect your {provider} account to grant access.",
        "Connected as {email} - saved with the form.": "Connected as {email} - saved with the form."
    }
}
</i18n>

<script>
import _ from 'lodash'
import { useSettingsStore, useCmsWizardStore, useAuthStore } from '@/stores'
import { adminClient as apolloClient } from '@/store/apollo'
import TestSmtpCredentialsMutation from '@/store/graphql/system/mutations/TestSmtpCredentials.gql'
import TestImapCredentialsMutation from '@/store/graphql/system/mutations/TestImapCredentials.gql'
import MailboxOAuthUrlQuery from '@/extensions/communication/store/graphql/queries/MailboxOAuthUrl.gql'
import SignatureApi from '@/extensions/communication/store/api/signature'

// Which scope initiated the in-flight OAuth connect (scope + provider + cms + pos).
// Module-level so it is SHARED across every mounted SmtpCredentials instance and
// SURVIVES a component remount during the consent round-trip (the wizard re-renders
// while the popup is open). Several instances listen on window 'message'; only the one
// whose context matches this consumes the popup result - no duplicate toasts, and the
// token/address are written to exactly the scope that connected.
let pendingOAuthConnect = null

export default {
    name: 'SmtpCredentials',
    props: {
        cms: {
            type: Object,
            required: true,
        },
        scope: {
            type: String,
            default: 'smtp',
            validator: value => ['smtp', 'newsletterSmtp', 'mailbox'].includes(value),
        },
        // Whether the communication feature is active. The inbound (IMAP), default
        // signature and POS-link sections are communication concerns - when the
        // feature is off this form is a pure SMTP sender (Auth + Sending only).
        communicationEnabled: {
            type: Boolean,
            default: true,
        },
        // The default-signature picker is a CMS/shop-level control. At a POS the
        // signature is per-POS (set on the POS form), since a shared mailbox can't
        // carry a per-store signature - so the POS drawer hides this section.
        showSignature: {
            type: Boolean,
            default: true,
        },
        storeModule: {
            type: String,
            default: 'settings/form',
        },
        // Inline mode: write changes straight onto cms[scope] instead of the
        // settings/wizard store (used to embed this form in the POS mailbox).
        inline: {
            type: Boolean,
            default: false,
        },
        // A SAVED POS's id (POS mailbox context) - lets OAuth write tokens directly
        // to that mailbox in the callback instead of the link-on-save flow. The POS↔
        // mailbox link itself is made by the POS save (one own mailbox per POS).
        pointOfSaleId: {
            type: [String, Number],
            default: null,
        },
    },
    data() {
        return {
            // Seeded from the model, NOT hardcoded false. The drawer that hosts
            // this form is destroy-on-close, so reopening it rebuilds the
            // component while the password typed into the previous visit is
            // still sitting unsaved in the model. Starting at false showed
            // "not changing the password" over a pending one, and the watcher
            // below then blanked it the moment the toggle was touched - a
            // credential lost between opening the drawer twice.
            changePasswordEnabled: !!_.get(this.cms, `${this.scope}.password`),
            testingSmtp: false,
            testingImap: false,
            connectingOauth: false,
            // The email the OAuth popup reported back, shown while the consent is
            // connected-but-not-yet-saved (link-on-save flow). NOTE: the popup Window
            // reference itself is a NON-reactive instance property (this.oauthPopup, set in
            // mounted) — Vue must never observe it: once the popup navigates to the provider
            // it is cross-origin, and reactivity touching any of its properties throws
            // "SecurityError: Permission denied ... on cross-origin object", which aborts
            // the Connect click handler so a re-click does nothing.
            oauthPendingEmail: null,
            testRecipientEmail: '',
            // null = follow the stored "has dedicated IMAP password" state; once the
            // user flips the switch this holds their explicit choice.
            separateImapPasswordOverride: null,
            signatures: [],
        }
    },
    created() {
        this.testRecipientEmail = this.defaultRecipientEmail
        if (this.isMailboxScope && _.get(this.cms, 'id')) {
            SignatureApi.fetchList(this.cms.id)
                .then(list => { this.signatures = list || [] })
                .catch(() => { /* signatures are optional */ })
        }
    },
    mounted() {
        // NON-reactive popup reference (see the data() note) — never on `data`, or Vue would
        // observe the cross-origin popup Window and throw a SecurityError on the next click.
        this.oauthPopup = null

        // The OAuth popup posts its result back here (see oauth-popup.blade.php).
        this.oauthMessageHandler = event => this.handleOauthMessage(event)
        window.addEventListener('message', this.oauthMessageHandler)
    },
    beforeDestroy() {
        if (this.oauthMessageHandler) window.removeEventListener('message', this.oauthMessageHandler)
    },
    watch: {
        changePasswordEnabled(value) {
            if (value) {
                this.$nextTick(() => {
                    const passwordInput = this.$refs.passwordInput
                    if (passwordInput) {
                        const inputElement = passwordInput.$el.getElementsByTagName('input')
                        if (inputElement.length) inputElement[0].select()
                    }
                })
            } else {
                this.setSmtpField('password', '')
            }
        },
    },
    computed: {
        isWizardMode() {
            return this.storeModule === 'cmsWizard';
        },
        // The IMAP inbox + default-signature apply to a real mailbox (CMS or POS),
        // but not to the send-only newsletter SMTP.
        isMailboxScope() {
            return this.scope !== 'newsletterSmtp';
        },
        // Inbound (IMAP), default signature and POS links only make sense for a real
        // mailbox AND only when the communication feature is on (they feed the
        // Messages inbox). With comm off this stays a pure SMTP sender.
        showMailboxSections() {
            return this.isMailboxScope && this.communicationEnabled;
        },
        // OAuth (Gmail / Microsoft 365) applies to every email channel kind - the
        // transactional mailbox, the comm mailbox, AND the send-only newsletter - even
        // though IMAP/receiving (isMailboxScope) does not apply to the newsletter.
        // It works in the CMS-creation wizard too: the consent nonce is scoped to the
        // creator and the parked tokens link when the new CMS is saved (link-on-save).
        showOAuth() {
            return true;
        },
        isExistingCredentials() {
            const id = _.get(this.cms, this.scope + '.id')
            return id && !String(id).startsWith('new:')
        },
        validationStatus() {
            return _.get(this.cms, this.scope + '.validation_status', null)
        },
        // IMAP has its own validation state, independent of the SMTP test.
        imapValidationStatus() {
            return _.get(this.cms, this.scope + '.imap_validation_status', null)
        },
        authMethod: {
            get() {
                return _.get(this.cms, this.scope + '.auth_method') || 'password'
            },
            set(value) {
                this.setSmtpField('auth_method', value)
            }
        },
        isOauth() {
            return this.authMethod === 'oauth_google' || this.authMethod === 'oauth_microsoft'
        },
        isOauthConnected() {
            return !!_.get(this.cms, this.scope + '.is_oauth_connected')
        },
        // Consent was just granted in the popup; the tokens are parked server-side
        // and will be linked to this mailbox when the form is saved.
        oauthPending() {
            return !!_.get(this.cms, this.scope + '.oauth_pending_token')
        },
        oauthProvider() {
            if (this.authMethod === 'oauth_google') return 'google'
            if (this.authMethod === 'oauth_microsoft') return 'microsoft'
            return null
        },
        // Org-wide admin consent is a Microsoft-only concept (Google has no equivalent
        // here), so the admin-approval action is offered for Microsoft 365 only.
        isMicrosoftOauth() {
            return this.oauthProvider === 'microsoft'
        },
        oauthProviderLabel() {
            return this.oauthProvider === 'microsoft' ? 'Microsoft 365' : 'Google'
        },
        oauthButtonLabel() {
            const verb = this.isOauthConnected ? this.$t('Reconnect') : this.$t('Connect')
            return `${verb} ${this.oauthProviderLabel}`
        },
        // Whether IMAP uses its own login. Defaults to the stored state; turning it
        // off tells the backend to reuse the SMTP username + password for IMAP too.
        useSeparateImapCredentials: {
            get() {
                if (this.separateImapPasswordOverride !== null) return this.separateImapPasswordOverride
                const hasDedicatedPassword = !!_.get(this.cms, this.scope + '.has_dedicated_imap_password')
                const hasDedicatedUsername = !!_.get(this.cms, this.scope + '.imap_username')
                return hasDedicatedPassword || hasDedicatedUsername
            },
            set(value) {
                this.separateImapPasswordOverride = value
                this.setSmtpField('imap_uses_smtp_password', !value)
                if (!value) {
                    this.setSmtpField('imap_username', '')
                    this.setSmtpField('imap_password', '')
                }
            }
        },
        authenticatedUserEmail() {
            return _.get(useAuthStore(), 'user.email', '')
        },
        defaultRecipientEmail() {
            return _.get(useAuthStore(), 'impersonator.email') || this.authenticatedUserEmail
        },
        isValidRecipientEmail() {
            if (!this.testRecipientEmail) {
                return false
            }
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.testRecipientEmail)
        },
        hasRequiredFields() {
            return !!(this.host && this.port && this.username)
        },
        // IMAP can connect once we have host + port and either a dedicated IMAP
        // password, the shared SMTP password, or stored credentials to fall back on.
        hasImapRequiredFields() {
            const hasSecret = !!(this.imapPassword || this.password || this.isExistingCredentials)
            return !!(this.imapHost && this.imapPort && hasSecret)
        },
        host: {
            get() {
                return _.get(this.cms, this.scope + '.host', '')
            },
            set(value) {
                this.setSmtpField('host', value)
            }
        },
        hostPlaceholder() {
            if (this.scope === 'newsletterSmtp') {
                return ''
            }
        },
        port: {
            get() {
                return _.get(this.cms, this.scope + '.port', '')
            },
            set(value) {
                this.setSmtpField('port', value ? parseInt(value) : null)
            }
        },
        portPlaceholder() {
            if (this.scope === 'newsletterSmtp') {
                return ''
            }
        },
        username: {
            get() {
                return _.get(this.cms, this.scope + '.username', '')
            },
            set(value) {
                this.setSmtpField('username', value)
            }
        },
        usernamePlaceholder() {
            if (this.scope === 'newsletterSmtp') {
                return ''
            }
        },
        password: {
            get() {
                return _.get(this.cms, this.scope + '.password', '')
            },
            set(value) {
                this.setSmtpField('password', value)
            }
        },
        mailgunWebhookSigningKey: {
            get() {
                return _.get(this.cms, this.scope + '.mailgun_webhook_signing_key', '')
            },
            set(value) {
                this.setSmtpField('mailgun_webhook_signing_key', value)
            }
        },
        imapEnabled: {
            get() {
                return !!_.get(this.cms, this.scope + '.imap_enabled')
            },
            set(value) {
                this.setSmtpField('imap_enabled', value)
            }
        },
        imapHost: {
            get() {
                return _.get(this.cms, this.scope + '.imap_host', '')
            },
            set(value) {
                this.setSmtpField('imap_host', value)
            }
        },
        imapPort: {
            get() {
                return _.get(this.cms, this.scope + '.imap_port', '')
            },
            set(value) {
                this.setSmtpField('imap_port', value ? parseInt(value) : null)
            }
        },
        imapUsername: {
            get() {
                return _.get(this.cms, this.scope + '.imap_username', '')
            },
            set(value) {
                this.setSmtpField('imap_username', value)
            }
        },
        imapEncryption: {
            get() {
                return _.get(this.cms, this.scope + '.imap_encryption') || 'ssl'
            },
            set(value) {
                this.setSmtpField('imap_encryption', value)
            }
        },
        imapPassword: {
            get() {
                return _.get(this.cms, this.scope + '.imap_password', '')
            },
            set(value) {
                this.setSmtpField('imap_password', value)
            }
        },
        defaultSignatureId: {
            get() {
                return _.get(this.cms, this.scope + '.defaultSignature.id') || null
            },
            set(value) {
                const signature = this.signatures.find(item => item.id === value)
                this.setSmtpField('defaultSignature', value ? { id: value, name: signature ? signature.name : '' } : { id: null })
            }
        },
        // Only offer the POS link control when there are points of sale to link to.
        address: {
            get() {
                return _.get(this.cms, this.scope + '.address', '')
            },
            set(value) {
                this.setSmtpField('address', value)
            }
        },
        addressPlaceholder() {
            if (this.scope === 'newsletterSmtp') {
                return ''
            }
        },
        name: {
            get() {
                return _.get(this.cms, this.scope + '.name', '')
            },
            set(value) {
                this.setSmtpField('name', value)
            }
        },
        namePlaceholder() {
            if (this.scope === 'newsletterSmtp') {
                return this.cms.name
            } else {
                return this.cms.name
            }
        },
    },
    methods: {
        setSmtpField(field, value) {
            if (this.inline) {
                const target = _.get(this.cms, this.scope)
                if (target) this.$set(target, field, value)
                return
            }
            if (this.isWizardMode) {
                const wizardStore = useCmsWizardStore();
                if (this.scope === 'smtp') {
                    const methodName = 'smtp' + field.charAt(0).toUpperCase() + field.slice(1).replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
                    if (typeof wizardStore[methodName] === 'function') {
                        wizardStore[methodName](value);
                    }
                } else if (this.scope === 'newsletterSmtp') {
                    const methodName = 'newsletterSmtp' + field.charAt(0).toUpperCase() + field.slice(1).replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
                    if (typeof wizardStore[methodName] === 'function') {
                        wizardStore[methodName](value);
                    }
                }
            } else {
                if (this.scope === 'smtp') {
                    useSettingsStore().setFormSmtpField(field, value)
                } else if (this.scope === 'newsletterSmtp') {
                    useSettingsStore().setFormNewsletterSmtpField(field, value)
                }
            }
        },
        testSmtpCredentials() {
            this.testingSmtp = true

            const smtpData = _.get(this.cms, this.scope, {})
            const recipientEmail = this.testRecipientEmail || this.authenticatedUserEmail

            apolloClient.mutate({
                mutation: TestSmtpCredentialsMutation,
                variables: {
                    SmtpCredentials: {
                        id: smtpData.id || null,
                        host: smtpData.host,
                        port: smtpData.port,
                        username: smtpData.username,
                        password: smtpData.password || null,
                        address: smtpData.address || null,
                        name: smtpData.name || null,
                    },
                    recipientEmail: recipientEmail,
                },
            })
            .then(({ data }) => {
                const result = data.TestSmtpCredentials
                if (result.success) {
                    this.$notify.success({
                        title: this.$t('SMTP test successful'),
                        message: result.message,
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 5000,
                    })
                    this.setSmtpField('validation_status', 'valid')
                } else {
                    this.$notify.error({
                        title: this.$t('SMTP test failed'),
                        message: result.message,
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                    this.setSmtpField('validation_status', 'invalid')
                }
            })
            .catch(error => {
                console.error(error)
                this.$notify.error({
                    title: this.$t('SMTP test failed'),
                    message: error.message,
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
            })
            .finally(() => {
                this.testingSmtp = false
            })
        },
        testImapCredentials() {
            this.testingImap = true

            const data = _.get(this.cms, this.scope, {})

            apolloClient.mutate({
                mutation: TestImapCredentialsMutation,
                variables: {
                    SmtpCredentials: {
                        id: data.id || null,
                        username: data.username || null,
                        password: data.password || null,
                        imap_host: data.imap_host || null,
                        imap_port: data.imap_port || null,
                        imap_username: data.imap_username || null,
                        imap_encryption: data.imap_encryption || null,
                        imap_password: data.imap_password || null,
                        // Honour the toggle live (before save): when reusing the SMTP
                        // password, ignore any stored dedicated IMAP password.
                        imap_uses_smtp_password: !this.useSeparateImapCredentials,
                    },
                },
            })
            .then(({ data: response }) => {
                const result = response.TestImapCredentials
                // Reflect the result in the IMAP badge (mirrors the SMTP test, which
                // sets validation_status). Local/optimistic - persists on save.
                this.setSmtpField('imap_validation_status', result.success ? 'valid' : 'invalid')
                this.$notify[result.success ? 'success' : 'error']({
                    title: result.success ? this.$t('IMAP test successful') : this.$t('IMAP test failed'),
                    message: result.message,
                    iconClass: result.success ? 'fas el-icon-fa-check' : 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: result.success ? 5000 : 8000,
                })
            })
            .catch(error => {
                console.error(error)
                this.$notify.error({
                    title: this.$t('IMAP test failed'),
                    message: error.message,
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
            })
            .finally(() => {
                this.testingImap = false
            })
        },
        // mode: 'user' (default) = normal per-user consent. 'admin' = the Microsoft
        // org-wide admin-consent flow, for tenants that block user consent. Both open a
        // popup; the callback distinguishes them and posts a status back.
        connectOauth(mode = 'user') {
            if (!this.oauthProvider) return
            const oauthMode = mode === 'admin' ? 'admin' : 'user'

            // `oauthPopup` is a NON-reactive instance property (see mounted) — it holds the
            // popup Window, which becomes cross-origin once it navigates to the provider, and
            // Vue observing it would throw a SecurityError on the next click. Drop the old
            // reference and open a brand-new `_blank` window each time (a fixed window name
            // would reuse a stale window stuck on about:blank).
            this.connectingOauth = false
            this.oauthPopup = null

            // Open the popup SYNCHRONOUSLY, right here in the click handler. window.open()
            // called later from the query's .then() is treated as non-user-initiated and
            // blocked by the popup blocker once the click's user-activation has expired —
            // racy, and reliably lost on a slow API — which surfaced as "Could not start
            // the connection". Opening a blank popup now (while the gesture is live) and
            // navigating it to the consent URL when the query returns keeps it trusted.
            const width = 520
            const height = 660
            const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2)
            const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2)
            const popup = window.open(
                '',
                '_blank',
                `width=${width},height=${height},left=${left},top=${top}`
            )
            if (!popup) {
                this.notifyOauthError() // blocker set to always-block for this site
                return
            }
            this.oauthPopup = popup
            this.connectingOauth = true

            // The CMS-creation wizard has no saved CMS yet (id is absent or a `new:`
            // placeholder) → pass null; the nonce is scoped to the creator and links
            // on save. Otherwise use the real CMS id.
            const cmsId = _.get(this.cms, 'id')
            const realCmsId = cmsId && !String(cmsId).startsWith('new')
                ? parseInt(cmsId, 10)
                : null

            // A saved POS → write tokens straight to its mailbox in the callback.
            const posId = this.pointOfSaleId && !String(this.pointOfSaleId).startsWith('new')
                ? parseInt(this.pointOfSaleId, 10)
                : null

            // Mark THIS scope as the initiator so only it consumes the popup result
            // (survives a remount; see pendingOAuthConnect).
            pendingOAuthConnect = { scope: this.scope, provider: this.oauthProvider, cmsId: realCmsId, pointOfSaleId: posId }

            apolloClient.query({
                query: MailboxOAuthUrlQuery,
                variables: {
                    cms_id: realCmsId,
                    provider: this.oauthProvider,
                    point_of_sale_id: posId,
                    mode: oauthMode,
                },
                fetchPolicy: 'network-only',
            })
            .then(({ data }) => {
                const url = data.EX_COMM_MailboxOAuthUrl
                if (!url) {
                    popup.close()
                    this.notifyOauthError()
                    return
                }
                // Navigate the already-open popup to the provider consent screen. The
                // form (and any unsaved edits) is never navigated away from; the popup
                // posts its result back via postMessage.
                popup.location = url
            })
            .catch(() => {
                popup.close()
                this.notifyOauthError()
            })
            .finally(() => { this.connectingOauth = false })
        },
        // Receive the popup result and park the one-time token on the mailbox; the
        // backend links the actual access/refresh tokens when the form is saved.
        handleOauthMessage(event) {
            // Only trust messages from this admin-ui origin - the popup result page
            // (/oauth/result) runs on the SAME origin now; the API renders no views.
            if (event.origin !== window.location.origin) return
            const message = event && event.data
            if (!message || message.source !== 'communication-oauth') return

            // Only the scope that initiated this connect consumes the result. All
            // SmtpCredentials instances listen on window 'message'; matching the
            // initiating context (which survives a remount during consent) means exactly
            // one handles it - no duplicate toasts, and the token/address land only on
            // the scope that connected.
            const cmsId = _.get(this.cms, 'id')
            const realCmsId = cmsId && !String(cmsId).startsWith('new') ? parseInt(cmsId, 10) : null
            const posId = this.pointOfSaleId && !String(this.pointOfSaleId).startsWith('new') ? parseInt(this.pointOfSaleId, 10) : null
            if (!pendingOAuthConnect
                || pendingOAuthConnect.scope !== this.scope
                || pendingOAuthConnect.provider !== this.oauthProvider
                || pendingOAuthConnect.cmsId !== realCmsId
                || pendingOAuthConnect.pointOfSaleId !== posId) {
                return
            }
            pendingOAuthConnect = null // claimed - no other instance will also process

            if (message.status === 'admin_consented') {
                // A tenant admin approved the app org-wide. No tokens yet — the mailbox
                // is signed in with a normal Connect next (which now succeeds silently).
                this.$notify.success({
                    title: this.$t('Organization approved'),
                    message: this.$t('Now click Connect to sign in the mailbox.'),
                    iconClass: 'fas el-icon-fa-check',
                    position: 'bottom-left', customClass: 'dark', duration: 8000,
                })
            } else if (message.status === 'expired') {
                // The one-time sign-in link had expired or was already used (e.g. a stale
                // popup, or the provider delivered the redirect twice). If a prior attempt
                // already connected, this is harmless; otherwise the user just retries.
                this.$notify.warning({
                    title: this.$t('Sign-in link expired'),
                    message: this.$t('Please click Connect again to finish signing in.'),
                    iconClass: 'fas el-icon-fa-triangle-exclamation',
                    position: 'bottom-left', customClass: 'dark', duration: 8000,
                })
            } else if (message.status === 'admin_consent_required') {
                // The tenant blocks user consent — point the user at the admin action.
                this.$notify.warning({
                    title: this.$t('Administrator approval required'),
                    message: this.$t('Your Microsoft administrator must approve this app once. Use "Approve for your organization".'),
                    iconClass: 'fas el-icon-fa-triangle-exclamation',
                    position: 'bottom-left', customClass: 'dark', duration: 10000,
                })
            } else if (message.status === 'connected') {
                this.oauthPendingEmail = message.email || null
                if (message.email) this.setSmtpField('address', message.email)
                // OAuth grants IMAP on the same token (two-way mailbox) - enable it.
                this.setSmtpField('imap_enabled', true)

                if (message.token) {
                    // New POS / CMS mailbox → link the parked tokens on save.
                    this.setSmtpField('oauth_pending_token', message.token)
                    this.$notify.success({
                        title: this.$t('Connected'),
                        message: this.$t('Save to finish.'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left', customClass: 'dark', duration: 6000,
                    })
                } else {
                    // Existing POS mailbox → tokens already written server-side; reflect
                    // the connected state locally (no save needed).
                    this.setSmtpField('is_oauth_connected', true)
                    this.setSmtpField('validation_status', 'valid')
                    this.$notify.success({
                        title: this.$t('Connected.'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left', customClass: 'dark', duration: 5000,
                    })
                }
            } else {
                this.notifyOauthError()
            }

            if (this.oauthPopup) {
                try { this.oauthPopup.close() } catch (error) { /* already closed */ }
                this.oauthPopup = null
            }
        },
        notifyOauthError() {
            this.$notify.error({
                title: this.$t('Could not start the connection'),
                iconClass: 'fas el-icon-fa-xmark',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            })
        },
    },
}
</script>

<style lang="sass" scoped>
    // Project hint convention: small, grey, tight line-height under the field
    // (el-form-item__content inherits 36px line-height otherwise).
    .smtp-credentials .help
        display: block
        line-height: 1.4
        font-size: .75rem
        color: #909399
        margin-top: 4px
    // Sectioned layout (CMS + POS mailbox): group the fields into clean cards
    // (Sending / Receiving / Signature). Whatever hosts this owns the padding, so
    // the component adds none of its own - a left inset here reads as a lopsided
    // drawer everywhere it is embedded, which is what the legacy accent bar did.
    // Double class to beat the base .smtp-credentials rule (equal specificity, later in file).
    .smtp-credentials.smtp-credentials--sectioned
        .smtp-header
            margin-bottom: 12px
        // The whole mailbox is ONE panel (carded by .api-keys); these are flat,
        // divider-separated groups inside it - not nested cards.
        .mailbox-section
            + .mailbox-section
                margin-top: 22px
                padding-top: 22px
                border-top: 1px solid #ebeef5
            // Subheader only labels its group - the section dividers separate them,
            // so no underline here (avoids the "label sandwiched between two lines").
            .mailbox-section__title
                display: flex
                align-items: center
                justify-content: space-between
                font-size: .72rem
                font-weight: 700
                text-transform: uppercase
                letter-spacing: .04em
                color: $color-grey-text
                margin: 0 0 16px
                .mailbox-section__label
                    display: flex
                    align-items: center
                    i
                        width: 1.1em
                        text-align: center
                        margin-right: 8px
                .smtp-validation-status .status-tag
                    height: 22px
                    display: flex
                    align-items: center
                    text-transform: none
                    letter-spacing: 0
                i
                    color: $--color-primary
            .smtp-test
                margin-top: 22px
            .imap-inbox
                margin-top: 0
                .imap-sep-toggle
                    margin-bottom: 16px
                .imap-test
                    margin-top: 16px
            .oauth-connect
                .oauth-status
                    margin-bottom: 14px
                    box-sizing: border-box
                    ::v-deep .el-alert__title
                        white-space: normal
                        line-height: 1.4
                .oauth-button
                    margin-bottom: 8px
                // Quiet, secondary admin-consent action under the primary Connect.
                .oauth-admin
                    margin-top: 10px
                    padding-top: 10px
                    border-top: 1px dashed #ebeef5
                    .help
                        display: block
                        margin-bottom: 2px
                    .oauth-admin__button
                        padding: 0
    .smtp-credentials
        margin-bottom: 22px
        position: relative
        .smtp-header
            display: flex
            align-items: center
            gap: 12px
            margin-bottom: 21px
            h4
                color: $color-grey-text
                margin: 0
            .smtp-validation-status
                display: flex
                align-items: center
                .status-tag
                    height: 24px
                    display: flex
                    align-items: center
        h4
            color: $color-grey-text
        .host-and-port
            display: grid
            grid-template-columns: auto 100px
            grid-gap: 1rem
            margin-bottom: 22px
            .el-form-item
                margin-bottom: 0

        // Three things share this row, and only two of them are always there:
        // turning "change password" on adds an input that a fixed two-column
        // grid had to squeeze into the half the switch already occupied, so the
        // switch label broke across two lines and the password box ended up
        // narrower than the username next to it. Flex with a sensible basis
        // keeps them side by side while they fit and drops the password field
        // onto its own line when they do not.
        .user-and-pass
            display: flex
            flex-wrap: wrap
            gap: 1rem
            margin-bottom: 22px
            .el-form-item
                margin-bottom: 0
            > .user
                flex: 1 1 180px
                min-width: 0

        .password-field
            display: flex
            align-items: flex-start
            gap: 20px
            flex: 1 1 300px
            min-width: 0
            .el-form-item
                margin: 0
            .switch
                // Never the thing that gives way: its label is two words and
                // reads as one control with the switch.
                flex: none
                padding-top: 20px
                ::v-deep
                    .el-form-item__content
                        min-height: 44px
                        display: flex
                        align-items: center
                    .el-switch__label
                        white-space: nowrap
            .pass
                // A floor, not a preference: the switch and the username both
                // give way before the box somebody is typing a password into.
                flex: 1 1 auto
                min-width: 180px
        .smtp-test
            display: flex
            align-items: flex-end
            gap: 10px
            margin-top: 12px
            .recipient-email
                flex-grow: 1
                margin-bottom: 0
            .test-button
                height: 32px
                margin-bottom: 2px
        .imap-inbox
            margin-top: 22px
            .switch
                margin-bottom: 0
            .imap-hint
                margin: 8px 0 16px
            .imap-pass
                margin-top: 22px
            .help
                line-height: 1.4

</style>
